import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../components/HeaderComponent";
import { Row, Column, Container, Label } from "../../components/grid/index";
import Card from "../../components/card/Card";
import Image from "../../components/image/Image";
import TextField from "../../components/textfield/TextField";
import {
  useProfile,
  useUserLogin,
  useManualUpload,
  useTheme,
} from "../../hooks/index";
import { IoEyeOffOutline, IoEyeOutline } from "react-icons/io5";
import { ImUser } from "react-icons/im";
import Toggle from "../../components/toggle/Toggle";
import { AuthContext } from "../../components/AuthContextComponent";
import defaultmode from "../../lib/images/DefaultMode.png";
import darkmode from "../../lib/images/DarkMode.png";
import DynamicTable from "../../components/table/DynamicTable";
import { forgetPassword } from "../../api/FetchAPI";
import { SecurityQuestionRequestArray } from "../../lib/interfaces/index";
import { ALERTMSG, STATUS } from "../../lib/enums/index";
import SecurityQuestion from "../../components/SecurityQuestion";
import UpdatePassword from "../../components/updatepassword/UpdatePassword";
import { AutoLogout } from "../../components/AutoLogoutComponent";
import LoadingComponent from "../../components/loading/Loading";
import useHeadComponent from "../../hooks/HeaderHook";
import { defaultHoverColor } from "../../lib/constants/values/Values";
import { FetchErr } from "../../lib/helpers/UnhandledError";
import { showAlert } from "../../promptMessages/PromptMessages";

const Profile = () => {
  const { section } = useParams<{ section: string }>();
  const {
    Balance,
    category,
    contactNo,
    email,
    fName,
    mName,
    lName,
    fullName,
    partnersName,
    errors,
    formValues,
    setSecretQuestion,
    handleClear,
    handleSubmit,
    handleChange,
  } = useProfile();

  const oldPasswordToggle = useUserLogin();
  const newPasswordToggle = useUserLogin();
  const confirmPasswordToggle = useUserLogin();
  const { customColor } = useUserLogin();
  const { theme, handleThemeSwitch } = useTheme();
  const { openOTP } = useManualUpload();
  const { customBannerImg } = useHeadComponent();
  const { userInfo } = useContext(AuthContext);
  const { username, setPartnerName } = useContext(AuthContext);
  const [isDone, setIsDone] = useState(false);
  const [redirectModal, setRedirectModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentQuestions, setCurrentQuestions] =
    useState<SecurityQuestionRequestArray | null>(null);
  const [note, setNote] = useState(false);
  const [showSecurityQuestion, setShowSecurityQuestion] = useState(false);
  const [isHoveredCurrentPass, setIsHoveredCurrentPass] = useState(false);
  const [isHoveredNewPass, setIsHoveredNewPass] = useState(false);
  const [isHoveredConfirmNewPass, setIsHoveredConfirmNewPass] = useState(false);
  const [questionLoading, setQuestionLoading] = useState(true);
  const themeBackground = userInfo?.theme === "DARK" ? "#24292e" : "";
  const themeTextColor = userInfo?.theme === "DARK" ? "#fafbfc" : "";
  const { pwdUpdate } = userInfo || {};

  const redirectState = !!(redirectModal && category === "EXTERNAL");

  const firstTimeLoginState = !!(pwdUpdate && category === "EXTERNAL");
  //effect to check the section and redirect if necessary
  useEffect(() => {
    if (!section) {
      navigate("/paymentsolution/profile/about");
    }
  }, [section, setSecretQuestion]);

  //effect to check if security question is set or null
  useEffect(() => {
    const fetchCurrentUserQuestion = async () => {
      setQuestionLoading(true); // Start loading

      forgetPassword(username)
        .then((response: any) => {
          const { respCode, respMsg, questionInfo } = response;

          if (respCode === 2000) {
            if (questionInfo === null || questionInfo.length < 2) {
              setCurrentQuestions(null);
              setNote(true);
              setRedirectModal(true);
            } else {
              setCurrentQuestions(questionInfo);
              setNote(false);
              setRedirectModal(false);
            }
          } else {
            showAlert(
              ALERTMSG.SOMEWENTWRONG,
              `${respMsg} (${respCode})`,
              "error",
              customColor,
              themeBackground,
              themeTextColor
            );
          }
        })
        .catch((error: any) => {
          FetchErr(error, themeBackground, customColor, themeTextColor);
        })
        .finally(() => {
          setQuestionLoading(false); // Always stop loading
        });
    };

    const timer = setTimeout(fetchCurrentUserQuestion, 1000);
    return () => clearTimeout(timer);
  }, [username, userInfo?.pwdUpdate, isDone, setIsDone]);

  const color = userInfo?.theme === "DARK" ? "#fafbfc" : "black";

  const isCurrentSection = (sect: string) => section === sect;
  const navigate = useNavigate();

  const handleNavigation = (sect: any) => {
    navigate(`/paymentsolution/profile/${sect}`);
  };

  useEffect(() => {
    setTimeout(async () => {
      setLoading(false);
    }, 3000);
  }, []);

  //component to render based on conditions for security question and password update
  const renderComponent = () => {
    if (questionLoading) return null; // Wait until fetch finishes

    if (pwdUpdate && category === STATUS.EXTERNAL) {
      if (
        section === "about" ||
        section === "theme" ||
        section === "secret-question"
      ) {
        return <UpdatePassword />;
      }
    }

    if (!pwdUpdate && category === STATUS.EXTERNAL) {
      if (redirectModal && (section === "about" || section === "theme")) {
        return <SecurityQuestion />;
      }

      if (section === "change-password") {
        if (!currentQuestions && category === STATUS.EXTERNAL) {
          return <SecurityQuestion />;
        }
      }
    }

    return null;
  };

  //handle show security question
  const handleShowSecurityQuestion = () => {
    setShowSecurityQuestion(true);
  };

  //hanlde mouse leave for password visibility toggles
  const handleMouseLeave = () => {
    oldPasswordToggle.handleMouseLeave();
    newPasswordToggle.handleMouseLeave();
    confirmPasswordToggle.handleMouseLeave();
    setIsHoveredCurrentPass(false);
    setIsHoveredNewPass(false);
    setIsHoveredConfirmNewPass(false);
  };

  return (
    <div className="font-sans" style={{ color }}>
      <Header Balance={Balance} theme={theme} />
      <AutoLogout
        openOTP={openOTP}
        redirectModal={redirectState}
        redirectChangePassword={firstTimeLoginState}
      />
      {loading ? (
        <>
          <div className="flex justify-center max-w-[1240px] mx-auto px-4 h-full items-center p-96">
            <LoadingComponent customColor={customColor} />
          </div>
        </>
      ) : (
        <>
          <Container>
            <Row>
              <Card
                marginTop={90}
                borderRight
                className="lg:h-[640px] lg:w-[300px] sm:h-[600px] sm:w-[250px] h-[505px] w-[200px] mx-auto transition-all duration-1000"
              >
                <Column gap={2} border itemPosition="center" paddingTop={1}>
                  <Image
                    rounded="full"
                    height={130}
                    width={130}
                    className="lg:w-28 lg:h-28 md:w-24 md:h-24 sm:w-20 sm:h-20 w-16 h-16"
                  >
                    <ImUser className="lg:text-3xl md:text-2xl sm:text-xl text-lg" />
                  </Image>
                  <Label
                    text={fullName}
                    fontWeight="semibold"
                    className="lg:text-custom-standard md:text-custom-petite sm:text-custom-micro text-custom-micro"
                  />
                  <Column marginTop={16} paddingTop={5}>
                    <button
                      onClick={() => handleNavigation("about")}
                      className={`text-left px-4 text-gray-700 bg-gray-300 hover:bg-slate-200 text-custom-micro font-medium transition-colors duration-300 focus:outline-none h-[30px] shadow-md font-sans `}
                      style={{
                        backgroundColor: isCurrentSection("about")
                          ? customColor || defaultHoverColor
                          : "",
                        color: isCurrentSection("about") ? "white" : "",
                      }}
                    >
                      About
                    </button>
                    <button
                      onClick={() => handleNavigation("change-password")}
                      className={`text-left px-4 text-gray-700 bg-gray-300 hover:bg-slate-200 text-custom-micro font-medium transition-colors duration-300 focus:outline-none h-[30px] shadow-md font-sans `}
                      style={{
                        backgroundColor: isCurrentSection("change-password")
                          ? customColor || defaultHoverColor
                          : "",
                        color: isCurrentSection("change-password")
                          ? "white"
                          : "",
                      }}
                    >
                      Change Password
                    </button>

                    <button
                      onClick={() => handleNavigation("secret-question")}
                      className={`text-left px-4 text-gray-700 bg-gray-300 hover:bg-slate-200 text-custom-micro font-medium transition-colors duration-300 focus:outline-none h-[30px] shadow-md font-sans`}
                      style={{
                        backgroundColor: isCurrentSection("secret-question")
                          ? customColor || defaultHoverColor
                          : "",
                        color: isCurrentSection("secret-question")
                          ? "white"
                          : "",
                      }}
                    >
                      Secret Question
                    </button>
                  </Column>
                </Column>
              </Card>

              <Card
                marginTop={90}
                className="lg:h-[640px] lg:w-[680px] md:h-[600px] md:w-[500px] sm:h-[600px] sm:w-[400px] h-auto w-64 mx-auto transition-all duration-1000"
                padding={5}
              >
                {isCurrentSection("change-password") && (
                  <>
                    <Column>
                      <Label
                        text={"Change Password"}
                        fontWeight="semibold"
                        size="16px"
                      />
                      <Row itemPosition="start">
                        <div className="bg-blue-50 border border-green-100 mt-5 rounded-md w-full">
                          <p className="text-md font-semibold pl-2 text-custom-petite text-black">
                            Password Creation Guide
                          </p>
                          <div
                            className={`pl-7 text-custom-micro ${
                              errors.currentPassword ||
                              errors.newPassword ||
                              errors.confirmNewPassword
                                ? "text-red-600"
                                : "text-green-900"
                            }`}
                          >
                            <li>
                              Contains alphanumeric and special characters.
                            </li>
                            <li>
                              Combination of uppercase and lowercase characters.
                            </li>
                            <li>
                              Password length must be a minimum of 8 characters.
                            </li>
                            <li>
                              Password length must not exceed 30 characters.
                            </li>
                            {/* <li>Common passwords are considered weak.</li> */}
                          </div>
                        </div>
                      </Row>
                      <Column
                        gap={5}
                        itemPosition="start"
                        paddingTop={1}
                        className="w-full"
                      >
                        <form
                          onSubmit={(e) =>
                            handleSubmit(e, handleShowSecurityQuestion)
                          }
                          className="lg:w-4/6 w-full"
                        >
                          <div className="mb-4">
                            <label
                              htmlFor="currentPassword"
                              className="block text-custom-micro font-medium"
                            >
                              Current Password
                            </label>
                            <div className="relative">
                              <input
                                type={
                                  oldPasswordToggle.showPassword
                                    ? "text"
                                    : "password"
                                }
                                id="currentPassword"
                                name="currentPassword"
                                value={formValues.currentPassword}
                                onChange={handleChange}
                                className={`mt-1 block w-full border rounded-md p-2 h-[30px] text-custom-petite text-black ${
                                  errors.currentPassword
                                    ? "border-red-500"
                                    : "border-gray-300"
                                } pr-10`}
                              />
                              <span
                                onMouseDown={oldPasswordToggle.handleMouseDown}
                                onMouseUp={oldPasswordToggle.handleMouseUp}
                                onMouseLeave={handleMouseLeave}
                                onMouseEnter={() =>
                                  setIsHoveredCurrentPass(true)
                                }
                                style={{
                                  color: isHoveredCurrentPass
                                    ? customColor
                                    : "",
                                  transition: "background-color 0.3s",
                                }}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer z-10 text-black hover:scale-125"
                              >
                                {oldPasswordToggle.showPassword ? (
                                  <IoEyeOffOutline />
                                ) : (
                                  <IoEyeOutline />
                                )}
                              </span>
                            </div>
                            {errors.currentPassword && (
                              <span className="text-red-500 text-xs">
                                {errors.currentPassword}
                              </span>
                            )}
                          </div>

                          <div className="mb-4">
                            <label
                              htmlFor="newPassword"
                              className="block text-custom-micro font-medium"
                            >
                              New Password
                            </label>
                            <div className="relative">
                              <input
                                type={
                                  newPasswordToggle.showPassword
                                    ? "text"
                                    : "password"
                                }
                                id="newPassword"
                                name="newPassword"
                                value={formValues.newPassword}
                                onChange={handleChange}
                                className={`mt-1 block w-full border rounded-md p-2 h-[30px] text-custom-petite text-black ${
                                  errors.newPassword
                                    ? "border-red-500"
                                    : "border-gray-300"
                                } pr-10`}
                              />
                              <span
                                onMouseDown={newPasswordToggle.handleMouseDown}
                                onMouseUp={newPasswordToggle.handleMouseUp}
                                onMouseLeave={handleMouseLeave}
                                onMouseEnter={() => setIsHoveredNewPass(true)}
                                style={{
                                  color: isHoveredNewPass ? customColor : "",
                                  transition: "background-color 0.3s",
                                }}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer z-10 text-black  hover:scale-125"
                              >
                                {newPasswordToggle.showPassword ? (
                                  <IoEyeOffOutline />
                                ) : (
                                  <IoEyeOutline />
                                )}
                              </span>
                            </div>
                            {errors.newPassword && (
                              <span className="text-red-500 text-xs">
                                {errors.newPassword}
                              </span>
                            )}
                          </div>

                          <div className="mb-4">
                            <label
                              htmlFor="confirmNewPassword"
                              className="block text-custom-micro font-medium"
                            >
                              Confirm New Password
                            </label>
                            <div className="relative">
                              <input
                                type={
                                  confirmPasswordToggle.showPassword
                                    ? "text"
                                    : "password"
                                }
                                id="confirmNewPassword"
                                name="confirmNewPassword"
                                value={formValues.confirmNewPassword}
                                onChange={handleChange}
                                className={`mt-1 block w-full border rounded-md p-2 h-[30px] text-custom-petite text-black ${
                                  errors.confirmNewPassword
                                    ? "border-red-500"
                                    : "border-gray-300"
                                } pr-10`}
                              />
                              <span
                                onMouseDown={
                                  confirmPasswordToggle.handleMouseDown
                                }
                                onMouseUp={confirmPasswordToggle.handleMouseUp}
                                onMouseLeave={handleMouseLeave}
                                onMouseEnter={() =>
                                  setIsHoveredConfirmNewPass(true)
                                }
                                style={{
                                  color: isHoveredConfirmNewPass
                                    ? customColor
                                    : "",
                                  transition: "background-color 0.3s",
                                }}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer z-10 text-black  hover:scale-125"
                              >
                                {confirmPasswordToggle.showPassword ? (
                                  <IoEyeOffOutline />
                                ) : (
                                  <IoEyeOutline />
                                )}
                              </span>
                            </div>
                            {errors.confirmNewPassword && (
                              <span className="text-red-500 text-xs">
                                {errors.confirmNewPassword}
                              </span>
                            )}
                          </div>

                          <div className="flex gap-2 ">
                            <button
                              type="button"
                              onClick={handleClear}
                              className="w-[100px] flex items-center justify-center bg-gray-300 text-gray-700 py-2 rounded-md hover:bg-gray-400 h-[30px] text-custom-micro hover:scale-105 transition-all duration-300"
                            >
                              Clear
                            </button>
                            <button
                              type="submit"
                              style={{ backgroundColor: customColor }}
                              className={`w-[100px] flex items-center justify-center  text-white py-2 rounded-md ${
                                customColor
                                  ? `hover:scale-105 transition-all duration-300`
                                  : `hover:shadow-lg hover:shadow-red-500/40 `
                              }   h-[30px] text-custom-micro`}
                            >
                              Change
                            </button>
                          </div>
                        </form>
                      </Column>
                    </Column>
                  </>
                )}

                {isCurrentSection("about") && (
                  <Column gap={5} itemPosition="start">
                    <Label
                      text={"Information"}
                      fontWeight="semibold"
                      size="xl"
                      position="left"
                    />
                    <Column gap={5} itemPosition="start">
                      <Row className="w-full justify-between">
                        <TextField
                          type="text"
                          label="First Name"
                          text={fName}
                          readonly
                        />
                        <TextField
                          type="text"
                          label="Middle Name"
                          text={mName}
                          readonly
                        />
                        <TextField
                          type="text"
                          label="Last Name"
                          text={lName}
                          readonly
                        />
                      </Row>
                      <TextField
                        type="text"
                        label="Email"
                        text={email}
                        readonly
                      />
                      <TextField
                        type="text"
                        label="Contact Number"
                        text={contactNo}
                        readonly
                      />
                      <TextField
                        type="text"
                        label="Business Name"
                        text={partnersName}
                        readonly
                      />
                      <TextField
                        type="text"
                        label="Category"
                        text={category}
                        readonly
                      />
                    </Column>
                  </Column>
                )}

                {isCurrentSection("theme") && (
                  <Column>
                    <Row>
                      <Label text="Theme" size="16px" fontWeight="semibold" />
                    </Row>
                    <Column gap={5}>
                      <Row className="lg:gap-52 md:gap-44 sm:gap-36 gap-28">
                        <Label text="Dark Mode" size="12px" />
                        <Toggle onClick={handleThemeSwitch} />
                      </Row>
                      <Row>
                        <Label
                          text={"Overview"}
                          fontWeight="semibold"
                          size="14px"
                        />
                      </Row>
                      <Row gap={20}>
                        <div className="flex flex-wrap gap-5 w-full">
                          <Column gap={5} itemPosition="center">
                            <Label text={"Default Mode"} size="12px" />
                            <Image
                              rounded="md"
                              className="lg:w-64 lg:h-40 md:w-52 md:h-32 sm:w-40 sm:h-24 w-32 h-20 items-center"
                            >
                              <img src={defaultmode} alt="defaultmode.png" />
                            </Image>
                          </Column>
                          <Column gap={5} itemPosition="center">
                            <Label text={"Dark Mode"} size="12px" />
                            <Image
                              rounded="md"
                              className="lg:w-64 lg:h-40 md:w-52 md:h-32 sm:w-40 sm:h-24 w-32 h-20 items-center"
                            >
                              <img src={darkmode} alt="darkmode.png" />
                            </Image>
                          </Column>
                        </div>
                      </Row>
                    </Column>
                  </Column>
                )}

                {isCurrentSection("secret-question") && (
                  <Column gap={5}>
                    <Row>
                      <Label
                        text="Secret Question"
                        size="16px"
                        fontWeight="semibold"
                      />
                    </Row>
                    <Row>
                      {note && (
                        <Label
                          text="***Have at least 2 Security Questions***"
                          size="12px"
                        />
                      )}
                    </Row>
                    <Row>
                      <DynamicTable setIsDone={setIsDone} />
                    </Row>
                  </Column>
                )}
              </Card>
            </Row>
          </Container>
        </>
      )}
      {renderComponent()}
    </div>
  );
};

export default Profile;
